import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useGiftWrapContext } from './GiftWrapContext';
import { useUserInput } from './UserInputContext.tsx';
import { getPageStepIndex, getReachablePagesFrom, hasAnyUserInput, hasRequiredUserInputForPage, isOptionSelectionPage, isVisualiserPage, normalizePageName } from './pages.js';
import { logError, logInfo } from '../utilities/logging.js';

export const PageContext = createContext({
  currentPage: 'firstPage',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  goToPage: (/** @type {string} */ _page) => {},
  pageSelections: {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getPageSelection: (/** @type {string} */ _pageName) => /** @type {number | null} */ (null),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setPageSelection: (/** @type {string} */ _pageName, /** @type {number | null} */ _index) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getPageAllSelected: /** @type {(pageName: string) => boolean} */ ((/** @type {string} */ _pageName) => false),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getPageHasSelections: /** @type {(pageName: string) => boolean} */ ((/** @type {string} */ _pageName) => false),
  resetPageContext: () => {}
});

export const PageProvider = ({ children }) => {
  const [currentPage, setCurrentPage] = useState('firstPage');
  // Store selections per page: { pageName: selectedIndex }
  const [pageSelections, setPageSelections] = useState(/** @type {{[key: string]: number}} */ ({}));
  const userInput = useUserInput();
  const finishPayloadRef = useRef(null);
  // Holds the MVVM cart item builder passed from UvInsertsFlow.onFinish.
  // Null when the regular gift wrap flow completes (no UV inserts active).
  const uvInsertCartItemBuilderRef = useRef(null);
  const {
    personalisationChoices,
    activeProduct,
    addToCartLoading,
    setGiftWrapModalOpen,
    resetGiftWrapState,
    setAddToCartLoading
  } = useGiftWrapContext();

  // Track last non-FINISHED page to capture context for visualiser payload.
  const lastNonFinishedPageRef = useRef('firstPage');
  useEffect(() => {
    if (currentPage !== 'FINISHED') {
      lastNonFinishedPageRef.current = currentPage;
    }
  }, [currentPage]);

  const getPageSelection = (pageName) => pageSelections[pageName] ?? null;

  /**
   * Determines if a given page currently has the required user selection/input.
   * @param {string} pageName - The page name.
   * @returns {boolean} True if the page has selections/input.
   */
  const getPageHasSelections = (pageName) => {
    if (isOptionSelectionPage(pageName)) {
      return getPageSelection(pageName) !== null;
    }

    if (isVisualiserPage(pageName)) {
      return hasRequiredUserInputForPage({ pageName, userInput });
    }

    return personalisationChoices.length > 0;
  };

  const getPageAllSelected = (pageName) => {
    return getPageHasSelections(pageName);
  };

  const setPageSelection = (pageName, index) => {
    setPageSelections((prev) => {
      if (index === null) {
        const { [pageName]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [pageName]: index };
    });
  };

  const isGoingBack = (targetPage) => {
    const currentIndex = getPageStepIndex(currentPage);
    const targetIndex = getPageStepIndex(targetPage);

    if (currentIndex === null || targetIndex === null) {
      return false;
    }

    return targetIndex < currentIndex;
  };

  const clearDownstreamSelections = (_targetPage) => {
    // No-op: preserve selections.
  };

  const resetPageContext = () => {
    setCurrentPage('firstPage');
    setPageSelections({});
  };

  const buildUserInputSnapshot = () => {
    const textInputs = typeof userInput?.getAllTextInputs === 'function' ? userInput.getAllTextInputs() : {};
    const imageInputs = typeof userInput?.getAllImageInputs === 'function' ? userInput.getAllImageInputs() : {};
    const visualiserSelection = userInput?.visualiserSelection || null;

    return {
      getTextInput: (key) => (textInputs?.[key] ? textInputs[key] : ''),
      getImageInput: (key) => (imageInputs?.[key] ? imageInputs[key] : null),
      getAllTextInputs: () => ({ ...textInputs }),
      getAllImageInputs: () => ({ ...imageInputs }),
      visualiserSelection
    };
  };

  /**
   * Navigates to the given page. When destination is 'FINISHED', an optional
   * uvInsertCartItemBuilder function can be passed — this is a closure provided by
   * UvInsertsFlow that builds a cart line item from MVVM data without touching window.giftWrapData.
   * @param {string} page - Destination page name.
   * @param {Function|null} [uvInsertCartItemBuilder] - Optional MVVM cart item builder.
   * @returns {void}
   */
  const goToPage = (page, uvInsertCartItemBuilder = null) => {
    const destinationPage = normalizePageName(page);
    // Capture the MVVM builder whenever we head to FINISHED.
    if (destinationPage === 'FINISHED') {
      uvInsertCartItemBuilderRef.current = uvInsertCartItemBuilder ?? null;
    }
    const goingBack = isGoingBack(destinationPage);

    // Treat firstPage -> addPersonalTouch as a new "personal touch journey".
    // This ensures the inserts/cards flow always reflects the latest gift wrap choices
    // and doesn't inherit stale selections or user inputs from a previous attempt.
    if (currentPage === 'firstPage' && destinationPage === 'addPersonalTouch') {
      setPageSelections({});
      userInput.clearAll();
    }

    if (goingBack) {
      clearDownstreamSelections(destinationPage);
      setCurrentPage(destinationPage);
      return;
    }

    // When leaving the flow pivot (addPersonalTouch), clear selections from the non-chosen branch.
    // This is derived from config reachability, rather than hardcoding flow page arrays.
    if (currentPage === 'addPersonalTouch' && destinationPage !== 'FINISHED') {
      const reachablePages = getReachablePagesFrom({ fromPage: destinationPage });

      setPageSelections((prev) => {
        const next = {};

        Object.entries(prev).forEach(([pageName, selectedIndex]) => {
          // Preserve the pivot selection, as it drives config-based navigation in the footer.
          if (pageName === 'addPersonalTouch') {
            // eslint-disable-next-line security/detect-object-injection
            next[pageName] = selectedIndex;
            return;
          }

          if (reachablePages.has(pageName)) {
            // eslint-disable-next-line security/detect-object-injection
            next[pageName] = selectedIndex;
          }
        });

        // Always reset the destination option selection on entry.
        if (isOptionSelectionPage(destinationPage) && destinationPage !== 'addPersonalTouch') {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars, security/detect-object-injection
          const { [destinationPage]: _ignored, ...rest } = next;
          return rest;
        }

        return next;
      });
    } else if (isOptionSelectionPage(destinationPage) && destinationPage !== 'addPersonalTouch') {
      // Reset option selection pages when navigating forward onto them.
      setPageSelections((prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, security/detect-object-injection
        const { [destinationPage]: _ignored, ...rest } = prev;
        return rest;
      });
    }

    if (destinationPage === 'FINISHED') {
      finishPayloadRef.current = {
        pageName: currentPage,
        userInputSnapshot: buildUserInputSnapshot()
      };
    }

    setCurrentPage(destinationPage);
  };

  /**
   * Effect to handle FINISHED page state.
   * When the page changes to FINISHED, trigger the add gift wrap routine.
   */
  useEffect(() => {
    if (currentPage === 'FINISHED') {
      const handleFinished = async () => {
        let fallbackTimeoutId = null;

        /**
         * Cleans up the cart update listener and closes the modal.
         * @returns {void}
         */
        const closeAfterCartUpdate = () => {
          if (fallbackTimeoutId) {
            window.clearTimeout(fallbackTimeoutId);
            fallbackTimeoutId = null;
          }

          document.removeEventListener('CART_DRAWER_UPDATED', closeAfterCartUpdate);
          setAddToCartLoading(false);
          setGiftWrapModalOpen(false);
        };

        try {
          setAddToCartLoading(true);
          document.addEventListener('CART_DRAWER_UPDATED', closeAfterCartUpdate);

          const finishPayload = finishPayloadRef.current;
          const pageName = finishPayload?.pageName || lastNonFinishedPageRef.current;
          const userInputSnapshot = finishPayload?.userInputSnapshot || null;
          const resolvedUserInput = userInputSnapshot || userInput;

          // Retrieve the MVVM cart item builder passed by UvInsertsFlow (if any).
          // Calling it here with a placeholder key; the real key is generated by giftWrapEventListener.
          // We pass the builder function itself so the listener can call it with the real key.
          const uvInsertCartItemBuilder = uvInsertCartItemBuilderRef.current;
          uvInsertCartItemBuilderRef.current = null;

          // Check whether there is any user input (text or images) in the UserInputContext.
          const hasAnyInput = hasAnyUserInput({ userInput: resolvedUserInput });

          // Also trigger if a UV insert config was active — the user doesn't need to have
          // typed anything or uploaded a photo for the insert to be added to the cart.
          const hasUvInsert = typeof uvInsertCartItemBuilder === 'function';

          logInfo({
            label: 'PageContext handleFinished',
            info: 'Computed visualiser input state',
            message: 'Computed whether the user has any visualiser input on finish.',
            extraData: { hasAnyInput, hasUvInsert, pageName }
          });

          if (personalisationChoices.length > 0 || hasAnyInput || hasUvInsert) {
            // Dispatch gift wrap event with user input context and page name.
            // Include the MVVM builder so giftWrapEventListener can build the cart item
            // directly from MVVM data, bypassing visualiserCartService and window.giftWrapData.
            const giftWrapEvent = new CustomEvent('GIFTWRAP_EVENT', {
              detail: {
                action: 'add_gift_wrap',
                personalisationChoices,
                userInput: resolvedUserInput,
                pageName,
                variantId: activeProduct?.id || '',
                product: {
                  title: activeProduct?.title || '',
                  price: (activeProduct?.price || 0) * 100 // Convert back to cents for consistency
                },
                uvInsertCartItemBuilder: uvInsertCartItemBuilder ?? null
              },
              bubbles: true
            });

            document.dispatchEvent(giftWrapEvent);

            // Fallback: if we never receive the cart update event, close the modal after a short delay.
            // This also removes the listener to prevent leaks.
            fallbackTimeoutId = window.setTimeout(() => {
              document.removeEventListener('CART_DRAWER_UPDATED', closeAfterCartUpdate);
              setAddToCartLoading(false);
              setGiftWrapModalOpen(false);
            }, 2000);
          } else {
            // No gift wrap or insert to add, close modal immediately.
            document.removeEventListener('CART_DRAWER_UPDATED', closeAfterCartUpdate);
            setGiftWrapModalOpen(false);
            setAddToCartLoading(false);
          }

          resetGiftWrapState();
          userInput.clearAll();
          resetPageContext();
          finishPayloadRef.current = null;
        } catch (error) {
          logError({
            label: 'PageContext handleFinished',
            // @ts-expect-error - unknown error shape.
            error,
            message: 'Failed to add gift wrap on FINISHED.',
            extraData: { currentPage: lastNonFinishedPageRef.current }
          });

          document.removeEventListener('CART_DRAWER_UPDATED', closeAfterCartUpdate);
          setAddToCartLoading(false);
          setGiftWrapModalOpen(false);
          finishPayloadRef.current = null;
        }
      };

      handleFinished().catch((error) => {
        logError({
          label: 'PageContext FINISHED effect',
          // @ts-expect-error - unknown error shape.
          error,
          message: 'Unhandled error while finalising gift wrap.',
          extraData: { currentPage: lastNonFinishedPageRef.current }
        });
      });
    }
  }, [
    currentPage,
    personalisationChoices,
    userInput,
    activeProduct,
    setGiftWrapModalOpen,
    resetGiftWrapState,
    setAddToCartLoading,
    resetPageContext
  ]);

  return (
    <PageContext.Provider
      value={{
        currentPage,
        goToPage,
        pageSelections,
        getPageSelection,
        setPageSelection,
        getPageAllSelected,
        getPageHasSelections,
        resetPageContext
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePage = () => {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error('usePage must be used within a PageProvider');
  }
  return context;
};
